import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTheme } from '../../context/ThemeContext';

// Pull-to-Refresh für den mobilen Scroll-Container. Wird ganz oben (scrollTop 0)
// nach unten gezogen → Gummiband-Indikator; über der Schwelle losgelassen ruft es
// `onRefresh()` (async). Native Touch-Listener mit { passive:false }, damit der
// Browser-eigene Overscroll-/Reload-Gestus unterdrückt werden kann.
// `ref` wird auf das echte Scroll-Element durchgereicht (App.js setzt scrollTop).

const TRIGGER = 68;   // px Pull bis Auslösung
const MAX = 96;       // max. sichtbarer Pull
const RESIST = 0.5;   // Gummiband-Widerstand

// Authentischer iOS-UIActivityIndicator: 12 verjüngte Speichen mit Opacity-
// Gradient rund um den Kreis. Beim Ziehen blendet er (in finaler Form) ein und
// skaliert leicht hoch; beim Laden rotiert er in 12 Stufen (steps(12)) → der
// helle „Kopf" jagt sauber um den Ring. Schlicht, grau, ohne Kapsel/Label.
function IosSpinner({ progress, spinning, color }) {
  const N = 12;
  const p = Math.min(1, Math.max(0, progress));
  return (
    <svg width="27" height="27" viewBox="0 0 24 24" aria-hidden="true"
         style={{
           opacity: spinning ? 1 : p,
           transform: spinning ? undefined : `scale(${0.78 + 0.22 * p})`,
           transformOrigin: '50% 50%',
           transition: spinning ? 'none' : 'opacity 90ms linear, transform 130ms cubic-bezier(.22,1,.36,1)',
           animation: spinning ? 'iosSpin 0.85s steps(12) infinite' : 'none',
         }}>
      {Array.from({ length: N }).map((_, i) => (
        <line key={i} x1="12" y1="3.6" x2="12" y2="8.4"
              stroke={color} strokeWidth="1.9" strokeLinecap="round"
              opacity={(i + 1) / N}
              transform={`rotate(${(i / N) * 360} 12 12)`} />
      ))}
      <style>{`@keyframes iosSpin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

const PullToRefresh = forwardRef(function PullToRefresh(
  { onRefresh, enabled = true, className, style, children, onScroll, fill = false },
  ref,
) {
  const { T } = useTheme();
  const scrollEl = useRef(null);
  useImperativeHandle(ref, () => scrollEl.current, []);

  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Refs für aktuelle Werte in den nativen Handlern (Closure-frisch).
  const startY = useRef(0);
  const active = useRef(false);
  const pullRef = useRef(0);
  const enabledRef = useRef(enabled);  enabledRef.current = enabled;
  const refreshingRef = useRef(false); refreshingRef.current = refreshing;
  const onRefreshRef = useRef(onRefresh); onRefreshRef.current = onRefresh;

  useEffect(() => {
    const el = scrollEl.current;
    if (!el) return;

    const onStart = (e) => {
      if (!enabledRef.current || refreshingRef.current || el.scrollTop > 0) {
        active.current = false;
        return;
      }
      startY.current = e.touches[0].clientY;
      active.current = true;
    };

    const onMove = (e) => {
      if (!active.current) return;
      if (el.scrollTop > 0) { active.current = false; pullRef.current = 0; setPull(0); return; }
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) { if (pullRef.current) { pullRef.current = 0; setPull(0); } return; }
      const dist = Math.min(MAX, dy * RESIST);
      pullRef.current = dist;
      setPull(dist);
      if (e.cancelable) e.preventDefault(); // nativen Overscroll/Reload unterdrücken
    };

    const onEnd = async () => {
      if (!active.current) return;
      active.current = false;
      const dist = pullRef.current;
      pullRef.current = 0;
      if (dist >= TRIGGER && !refreshingRef.current) {
        setRefreshing(true);
        setPull(TRIGGER * 0.6);
        const started = Date.now();
        try { await onRefreshRef.current?.(); } catch {}
        const wait = Math.max(0, 480 - (Date.now() - started)); // Anti-Flacker
        if (wait) await new Promise(r => setTimeout(r, wait));
        setRefreshing(false);
        setPull(0);
      } else {
        setPull(0);
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  const progress = pull / TRIGGER;
  const dragging = active.current;

  return (
    <div style={{ position: 'relative', overflow: 'hidden', height: fill ? '100%' : undefined }}>
      <div style={{
        position: 'absolute', top: 'env(safe-area-inset-top, 0px)', left: 0, right: 0,
        height: Math.max(0, pull), zIndex: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        <IosSpinner progress={progress} spinning={refreshing} color={T.sub} />
      </div>
      <div
        ref={scrollEl}
        className={className}
        onScroll={onScroll}
        style={{
          ...style,
          transform: pull ? `translateY(${pull}px)` : undefined,
          transition: dragging ? 'none' : 'transform 300ms cubic-bezier(.22,1,.36,1)',
        }}
      >
        {children}
      </div>
    </div>
  );
});

export default PullToRefresh;
